import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useLeadModal } from "../context/LeadModalContext";
import { Bot, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadCaptureModal() {
    const { open, setOpen, closeModal, source } = useLeadModal();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", company: "", need: "" });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.need) {
            toast.error("Please fill in name, email, and what you need.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/leads`, {
                name: form.name,
                email: form.email,
                company: form.company || null,
                message: form.need,
                source: `modal:${source || "cta"}`,
            });
            setSubmitted(true);
            toast.success("You're in. We'll reach out within 1 business day.");
        } catch (err) {
            const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
            toast.error(typeof detail === "string" ? detail : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setSubmitted(false);
        setForm({ name: "", email: "", company: "", need: "" });
        closeModal();
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                setOpen(v);
                if (!v) setTimeout(() => setSubmitted(false), 250);
            }}
        >
            <DialogContent
                data-testid="lead-capture-modal"
                className="sm:max-w-[520px] bg-[#0a1325] border border-[#00f0ff]/20 text-white p-0 overflow-hidden"
            >
                <div className="absolute inset-0 pointer-events-none opacity-40 grid-bg" />
                <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#00f0ff]/10 blur-3xl pointer-events-none" />

                {!submitted ? (
                    <form onSubmit={onSubmit} className="relative p-8">
                        <DialogHeader className="text-left mb-6">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00f0ff]/10 border border-[#00f0ff]/30 w-fit mb-4">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#00f0ff]" />
                                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#00f0ff]">
                                    Let&apos;s Build
                                </span>
                            </div>
                            <DialogTitle className="font-heading text-3xl font-bold tracking-tight">
                                Let&apos;s build your bot.
                            </DialogTitle>
                            <DialogDescription className="text-[#94a3b8] mt-2">
                                Tell us what you&apos;re trying to automate. We&apos;ll send a custom plan & demo within 24 hours.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <Label htmlFor="lead-name" className="text-xs uppercase tracking-wider text-[#94a3b8]">
                                        Name
                                    </Label>
                                    <Input
                                        id="lead-name"
                                        data-testid="lead-input-name"
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        placeholder="Alex Rivera"
                                        className="bg-[#050b14] border-white/10 text-white placeholder:text-[#475569] focus-visible:ring-[#00f0ff] focus-visible:border-[#00f0ff]"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="lead-email" className="text-xs uppercase tracking-wider text-[#94a3b8]">
                                        Email
                                    </Label>
                                    <Input
                                        id="lead-email"
                                        type="email"
                                        data-testid="lead-input-email"
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        placeholder="alex@company.com"
                                        className="bg-[#050b14] border-white/10 text-white placeholder:text-[#475569] focus-visible:ring-[#00f0ff] focus-visible:border-[#00f0ff]"
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="lead-company" className="text-xs uppercase tracking-wider text-[#94a3b8]">
                                    Company <span className="text-[#475569] normal-case">(optional)</span>
                                </Label>
                                <Input
                                    id="lead-company"
                                    data-testid="lead-input-company"
                                    value={form.company}
                                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                                    placeholder="Solopreneur, Acme Inc., etc."
                                    className="bg-[#050b14] border-white/10 text-white placeholder:text-[#475569] focus-visible:ring-[#00f0ff] focus-visible:border-[#00f0ff]"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="lead-need" className="text-xs uppercase tracking-wider text-[#94a3b8]">
                                    What do you want to build?
                                </Label>
                                <Textarea
                                    id="lead-need"
                                    data-testid="lead-input-need"
                                    value={form.need}
                                    onChange={(e) => setForm({ ...form, need: e.target.value })}
                                    rows={4}
                                    placeholder="e.g., A 24/7 customer support agent that handles refunds and FAQs from my Shopify store."
                                    className="bg-[#050b14] border-white/10 text-white placeholder:text-[#475569] focus-visible:ring-[#00f0ff] focus-visible:border-[#00f0ff] resize-none"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            data-testid="lead-submit-btn"
                            disabled={submitting}
                            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#00f0ff] text-[#050b14] font-bold hover:bg-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
                        >
                            {submitting ? (
                                <>
                                    <Bot className="w-4 h-4 animate-spin" />
                                    Sending…
                                </>
                            ) : (
                                <>
                                    Start My Build
                                    <ArrowRight className="w-4 h-4" />
                                </>
                            )}
                        </button>
                        <p className="mt-3 text-[11px] text-center text-[#64748b]">
                            No commitment to start · We never share your data
                        </p>
                    </form>
                ) : (
                    <div className="relative p-10 text-center" data-testid="lead-success">
                        <div className="mx-auto w-16 h-16 rounded-full bg-[#00f0ff]/15 border border-[#00f0ff]/40 flex items-center justify-center mb-5">
                            <Check className="w-7 h-7 text-[#00f0ff]" strokeWidth={2.5} />
                        </div>
                        <h3 className="font-heading text-2xl font-bold mb-2">You&apos;re on the list.</h3>
                        <p className="text-[#94a3b8] max-w-sm mx-auto">
                            We just sent a confirmation to <span className="text-white font-medium">{form.email}</span>.
                            Expect a custom plan & live demo within 1 business day.
                        </p>
                        <button
                            onClick={reset}
                            data-testid="lead-success-close"
                            className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-lg border border-white/15 text-white hover:border-[#00f0ff] hover:text-[#00f0ff] transition-all"
                        >
                            Close
                        </button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
